import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import humanTime from 'flarum/common/helpers/humanTime';
import { api } from '../../common/utils/api';
import MpSkeleton from './MpSkeleton';
/**
 * Aba "Reviews" do perfil: avaliacoes que o usuario ESCREVEU (de produto e de
 * vendedor), separada da pagina de vendedor (que mostra as recebidas). Lista
 * publica das aprovadas, com nota, alvo e data. No proprio perfil, o autor pode
 * editar ou excluir cada avaliacao que escreveu (recebidas nao sao editaveis).
 */
export default class ReviewsProfileSection extends Component {
  loading = false;
  data = null;
  fetchedFor = 0;
  editingId = null;
  editRating = 5;
  editBody = '';
  saving = false;
  t(k, vars) {
    return app.translator.trans('ramon-marketplace.forum.user_reviews.' + k, vars);
  }
  ensureLoaded(userId) {
    if (!userId || this.fetchedFor === userId) return;
    this.fetchedFor = userId;
    this.loading = true;
    this.data = null;
    this.editingId = null;
    api('GET', '/marketplace/users/' + userId + '/reviews').then(r => {
      this.data = r.data ?? [];
    }).catch(() => {
      this.data = [];
    }).then(() => {
      this.loading = false;
      m.redraw();
    });
  }

  /** O visitante e o dono do perfil (pode gerenciar as proprias avaliacoes)? */
  isOwner() {
    const user = this.attrs.user;
    const me = app.session.user;
    return !!(me && user && typeof user.id === 'function' && Number(me.id()) === Number(user.id()));
  }

  /** Recebidas sao de outras pessoas: nao editaveis. So produto/vendedor escritas. */
  canManage(r) {
    return this.isOwner() && (r.type === 'product' || r.type === 'seller');
  }
  startEdit(r) {
    this.editingId = r.id;
    this.editRating = r.rating;
    this.editBody = r.body ?? '';
  }
  cancelEdit() {
    this.editingId = null;
    this.saving = false;
  }
  async saveEdit(r) {
    this.saving = true;
    m.redraw();
    try {
      if (r.type === 'product' && r.target_id) {
        await api('POST', `/marketplace/products/${r.target_id}/reviews`, {
          rating: this.editRating,
          comment: this.editBody
        });
      } else if (r.type === 'seller' && r.seller_id) {
        await api('POST', `/marketplace/sellers/${r.seller_id}/reviews`, {
          rating: this.editRating,
          body: this.editBody,
          product_id: r.product_id ?? null
        });
      } else {
        throw new Error('unsupported');
      }
      app.alerts.show({
        type: 'success'
      }, this.t('saved'));
      this.editingId = null;
      this.reload();
    } catch (e) {
      app.alerts.show({
        type: 'error'
      }, this.t('save_failed'));
    } finally {
      this.saving = false;
      m.redraw();
    }
  }
  async deleteReview(r) {
    if (!confirm(this.t('confirm_delete'))) return;
    const path = r.type === 'seller' ? `/marketplace/seller-reviews/${r.review_id}` : `/marketplace/reviews/${r.review_id}`;
    try {
      await api('DELETE', path);
      app.alerts.show({
        type: 'success'
      }, this.t('deleted'));
      if (this.editingId === r.id) this.editingId = null;
      this.reload();
    } catch (e) {
      app.alerts.show({
        type: 'error'
      }, this.t('delete_failed'));
    }
  }

  /** Recarrega a lista apos editar/excluir. */
  reload() {
    const user = this.attrs.user;
    if (!user || typeof user.id !== 'function') return;
    this.fetchedFor = 0;
    this.ensureLoaded(Number(user.id()));
  }
  stars(rating) {
    return m("span", {
      className: "MpUserReviews-stars"
    }, [1, 2, 3, 4, 5].map(n => m("i", {
      key: n,
      className: 'fas fa-star' + (n <= rating ? ' is-on' : '')
    })));
  }
  ratePicker() {
    return m("span", {
      className: "MpUserReviews-ratePick"
    }, [1, 2, 3, 4, 5].map(n => m("i", {
      key: n,
      className: 'fas fa-star' + (n <= this.editRating ? ' is-on' : ''),
      onclick: () => this.editRating = n
    })));
  }
  view() {
    const user = this.attrs.user;
    if (!user || typeof user.id !== 'function') return null;
    this.ensureLoaded(Number(user.id()));
    if (this.loading && !this.data) {
      return m("section", {
        className: "MpUserReviews"
      }, m(MpSkeleton, {
        variant: "list"
      }));
    }
    const items = this.data ?? [];
    return m("section", {
      className: "MpUserReviews"
    }, m("div", {
      className: "MpUserReviews-head"
    }, m("h4", {
      className: "MpUserReviews-title"
    }, this.t('title')), items.length > 0 && m("span", {
      className: "MpUserReviews-count"
    }, items.length)), items.length === 0 ? m("p", {
      className: "MpUserReviews-empty"
    }, this.t('empty')) : m("div", {
      className: "MpUserReviews-list"
    }, items.map(r => this.renderReview(r))));
  }
  renderReview(r) {
    const icon = r.type === 'product' ? 'fa-box' : r.type === 'received' ? 'fa-inbox' : 'fa-store';
    let target;
    if (r.type === 'product' && r.target_id) {
      target = m("a", {
        href: app.route('marketplace.product', {
          id: r.target_id
        }),
        config: m.route.link
      }, r.target_name);
    } else if (r.type === 'received') {
      target = m("span", null, r.target_name || '?', " ", m("span", {
        className: "MpUserReviews-tag"
      }, this.t('received_label')));
    } else {
      target = m("span", null, r.target_name || '?');
    }
    const editing = this.editingId === r.id;
    return m("div", {
      className: "MpUserReviews-item",
      key: r.id
    }, m("span", {
      className: 'MpUserReviews-icon MpUserReviews-icon--' + r.type
    }, m("i", {
      className: 'fas ' + icon
    })), m("div", {
      className: "MpUserReviews-body"
    }, m("div", {
      className: "MpUserReviews-top"
    }, editing ? this.ratePicker() : this.stars(r.rating), m("span", {
      className: "MpUserReviews-target"
    }, target), r.created_at && m("span", {
      className: "MpUserReviews-date"
    }, humanTime(new Date(r.created_at))), this.canManage(r) && !editing && m("span", {
      className: "MpUserReviews-actions"
    }, m("button", {
      type: "button",
      className: "Button Button--link MpUserReviews-action",
      onclick: () => this.startEdit(r)
    }, this.t('edit')), m("button", {
      type: "button",
      className: "Button Button--link MpUserReviews-action MpUserReviews-action--danger",
      onclick: () => this.deleteReview(r)
    }, this.t('delete')))), editing ? m("div", {
      className: "MpUserReviews-edit"
    }, m("textarea", {
      className: "FormControl MpUserReviews-textarea",
      rows: 3,
      maxlength: 2000,
      placeholder: this.t('edit_placeholder'),
      value: this.editBody,
      oninput: e => this.editBody = e.target.value
    }), m("div", {
      className: "MpUserReviews-editActions"
    }, m(Button, {
      className: "Button Button--primary Button--sm",
      loading: this.saving,
      onclick: () => this.saveEdit(r)
    }, this.t('save')), m(Button, {
      className: "Button Button--sm",
      disabled: this.saving,
      onclick: () => this.cancelEdit()
    }, this.t('cancel')))) : r.body && m("p", {
      className: "MpUserReviews-text"
    }, r.body)));
  }
}
flarum.reg.add('ramon-marketplace', 'forum/components/ReviewsProfileSection', ReviewsProfileSection);